export const physiology3Module = {
  id: "physiology3",
  title: "Renal Physiology: Part 3",
  sections: [
    // ── Acid-Base Basics ─────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Acid-Base Balance — Basics</h3>
      <p>Blood pH is maintained within a tight range of <strong>7.35–7.45</strong>. Values below 7.35 are acidaemia; above 7.45 are alkalaemia. The body uses three defence systems:</p>
      <ol>
        <li><b class="purple-term">Chemical buffers</b> — instant (seconds): bicarbonate/CO₂, phosphate, proteins (haemoglobin)</li>
        <li><b class="purple-term">Respiratory compensation</b> — fast (minutes to hours): adjust CO₂ by changing ventilation</li>
        <li><b class="purple-term">Renal compensation</b> — slow (hours to days): excrete/retain H⁺ or HCO₃⁻</li>
      </ol>
      <p>The relationship is described by the <b class="purple-term">Henderson-Hasselbalch equation</b>:</p>
      <p style="text-align:center;"><strong>pH = 6.1 + log([HCO₃⁻] / (0.0307 × PaCO₂))</strong></p>
      <p>This shows that pH depends on the <strong>ratio</strong> of HCO₃⁻ (controlled by kidneys) to CO₂ (controlled by lungs).</p>
      <div class="highlight-box">Normal arterial blood gas (ABG) values:
        <ul>
          <li>pH: <em>7.35–7.45</em></li>
          <li>PaCO₂: <em>35–45 mmHg (4.7–6.0 kPa)</em></li>
          <li>HCO₃⁻: <em>22–28 mmol/L</em></li>
          <li>Base excess: <em>−2 to +2 mmol/L</em></li>
        </ul>
      </div>
      <p><strong>Interpreting an ABG — a systematic approach:</strong></p>
      <ol>
        <li>Is the pH acidaemic or alkalaemic?</li>
        <li>Is the primary disorder respiratory (PaCO₂ abnormal) or metabolic (HCO₃⁻ abnormal)?</li>
        <li>Is there appropriate compensation?</li>
        <li>If metabolic acidosis: calculate the <b class="purple-term">anion gap</b> = Na⁺ − (Cl⁻ + HCO₃⁻). Normal: 8–12 mmol/L.</li>
      </ol>`,
    },
    {
      type: "question",
      text: "A patient has pH 7.28, PaCO₂ 28 mmHg, HCO₃⁻ 13 mmol/L. What is the primary disorder and what does the low PaCO₂ indicate?",
      options: [
        "Respiratory acidosis with metabolic compensation",
        "Metabolic alkalosis with respiratory compensation",
        "Metabolic acidosis with respiratory compensation (hyperventilation to blow off CO₂)",
        "Respiratory alkalosis with metabolic compensation",
      ],
      correct: 2,
      explanation:
        "pH 7.28 = acidaemia. Low HCO₃⁻ (13 mmol/L) confirms the primary disorder is metabolic acidosis. The low PaCO₂ (28 mmHg, below normal 35–45) shows appropriate respiratory compensation: the patient is hyperventilating (Kussmaul breathing) to blow off CO₂ and partially restore pH.",
    },
    {
      type: "question",
      text: "What does the anion gap measure, and what is its normal value?",
      options: [
        "The difference between serum sodium and potassium; normal 0–4 mmol/L",
        "The unmeasured anions in plasma (mainly proteins and phosphate); normal 8–12 mmol/L",
        "The difference between serum bicarbonate and chloride; normal 20–30 mmol/L",
        "The ratio of respiratory to metabolic acid load; normal 1.0",
      ],
      correct: 1,
      explanation:
        "Anion gap = Na⁺ − (Cl⁻ + HCO₃⁻). It reflects unmeasured anions in plasma — mainly albumin, phosphate, and sulphate. The normal value is 8–12 mmol/L. A raised anion gap (> 12) in metabolic acidosis indicates an acid with an unmeasured anion has been added to the blood (e.g. lactate, ketones, uraemic acids).",
    },

    // ── The 4 Disorders ──────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>The Four Acid-Base Disorders</h3>
      <p><b class="purple-term">Metabolic acidosis</b> (↓HCO₃⁻ → ↓pH):
      <br/>Respiratory compensation: hyperventilation → ↓PaCO₂ (expected PaCO₂ = 1.5 × HCO₃⁻ + 8 ± 2, <em>Winter's formula</em>)</p>
      <p>Classify by anion gap:</p>
      <ul>
        <li><strong>High anion gap (HAGMA)</strong>: an unmeasured acid replaces HCO₃⁻. Mnemonic: <b class="purple-term">MUDPILES</b> — Methanol, Uraemia, DKA, Propylene glycol, Isoniazid/Iron, Lactic acidosis, Ethylene glycol, Salicylates</li>
        <li><strong>Normal anion gap (NAGMA / hyperchloraemic)</strong>: HCO₃⁻ lost and replaced by Cl⁻. Mnemonic: <b class="purple-term">HARDASS</b> — Hyperalimentation, Addison's, Renal tubular acidosis, Diarrhoea, Acetazolamide, Spironolactone, Saline infusion</li>
      </ul>
      <p><b class="purple-term">Metabolic alkalosis</b> (↑HCO₃⁻ → ↑pH):
      <br/>Respiratory compensation: hypoventilation → ↑PaCO₂
      <br/>Causes: vomiting/NG suction (H⁺ loss), loop/thiazide diuretics (hypokalaemia drives H⁺ into cells), primary hyperaldosteronism, antacid overdose</p>
      <p><b class="purple-term">Respiratory acidosis</b> (↑PaCO₂ → ↓pH):
      <br/>Metabolic compensation: kidneys retain HCO₃⁻ (takes 3–5 days for full compensation)
      <br/>Causes: hypoventilation — COPD, obesity-hypoventilation, neuromuscular disease (GBS, MG), respiratory failure, opioid/sedative overdose</p>
      <p><b class="purple-term">Respiratory alkalosis</b> (↓PaCO₂ → ↑pH):
      <br/>Metabolic compensation: kidneys excrete HCO₃⁻
      <br/>Causes: hyperventilation — anxiety/panic, pain, PE, pulmonary oedema, altitude, hepatic encephalopathy, salicylate toxicity (early phase)</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Compensation rules (approximate)</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">Metabolic acidosis</b>: PaCO₂ ↓ by 1.2 mmHg per 1 mmol/L ↓ HCO₃⁻</li>
            <li><b class="purple-term">Metabolic alkalosis</b>: PaCO₂ ↑ by 0.7 mmHg per 1 mmol/L ↑ HCO₃⁻</li>
            <li><b class="purple-term">Respiratory acidosis</b>: HCO₃⁻ ↑ by 1 (acute) or 3.5 (chronic) per 10 mmHg ↑ PaCO₂</li>
            <li><b class="purple-term">Respiratory alkalosis</b>: HCO₃⁻ ↓ by 2 (acute) or 5 (chronic) per 10 mmHg ↓ PaCO₂</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "A patient is vomiting profusely. ABG shows pH 7.52, PaCO₂ 48 mmHg, HCO₃⁻ 38 mmol/L. What is the diagnosis?",
      options: [
        "Respiratory alkalosis with metabolic compensation",
        "Metabolic alkalosis with respiratory compensation",
        "Metabolic acidosis with respiratory compensation",
        "Respiratory acidosis with metabolic compensation",
      ],
      correct: 1,
      explanation:
        "pH 7.52 = alkalaemia. High HCO₃⁻ (38 mmol/L) indicates the primary disorder is metabolic alkalosis. Vomiting causes H⁺ loss (HCl in gastric juice) → HCO₃⁻ rises. The mildly elevated PaCO₂ (48 mmHg) represents appropriate hypoventilation (respiratory compensation) to retain CO₂ and partially lower the pH.",
    },
    {
      type: "question",
      text: "A patient with COPD has a chronic ABG showing pH 7.36, PaCO₂ 60 mmHg, HCO₃⁻ 34 mmol/L. What is the diagnosis?",
      options: [
        "Acute respiratory acidosis — uncompensated",
        "Metabolic alkalosis with respiratory compensation",
        "Chronic respiratory acidosis with full renal compensation",
        "Mixed metabolic and respiratory acidosis",
      ],
      correct: 2,
      explanation:
        "The high PaCO₂ (60 mmHg) indicates respiratory acidosis — the lungs cannot eliminate CO₂ (COPD). The high HCO₃⁻ (34 mmol/L) and near-normal pH indicate full renal compensation — the kidneys have retained HCO₃⁻ over days to weeks to buffer the excess CO₂. This is a compensated chronic respiratory acidosis.",
    },
    {
      type: "question",
      text: "A patient in DKA has a high anion gap metabolic acidosis. Which mnemonic helps recall high anion gap causes?",
      options: ["HARDASS", "MUDPILES", "AEIOU", "WHHHHIMP"],
      correct: 1,
      explanation:
        "MUDPILES covers the main causes of high anion gap metabolic acidosis: Methanol, Uraemia, Diabetic ketoacidosis (DKA), Propylene glycol, Isoniazid/Iron, Lactic acidosis, Ethylene glycol, Salicylates. HARDASS covers normal anion gap (hyperchloraemic) metabolic acidosis causes.",
    },

    // ── Renal Acid-Base & RTA ────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Renal Acid-Base Mechanisms & Renal Tubular Acidosis (RTA)</h3>
      <p>The kidneys regulate pH through three main mechanisms:</p>
      <p>1. <b class="purple-term">HCO₃⁻ reabsorption in the PCT</b>: ~85% of filtered HCO₃⁻ is reabsorbed via NHE3 and carbonic anhydrase (as covered in Part 1). This prevents HCO₃⁻ loss — not net acid excretion, but preservation of base.</p>
      <p>2. <b class="purple-term">Titratable acid excretion</b>: H⁺ secreted in the collecting duct binds to phosphate (HPO₄²⁻ → H₂PO₄⁻) and is excreted as titratable acid. This directly removes H⁺.</p>
      <p>3. <b class="purple-term">Ammonium (NH₄⁺) excretion</b>: PCT cells generate NH₃ from glutamine → NH₃ combines with secreted H⁺ → NH₄⁺, which is excreted in the urine. This is the major mechanism of H⁺ excretion, especially in chronic acidosis.</p>
      <p><strong>Renal tubular acidosis (RTA)</strong> is a group of disorders where the kidney cannot adequately acidify the urine:</p>
      <ul>
        <li><b class="purple-term">Type 1 (Distal RTA)</b>: collecting duct cannot secrete H⁺ → urine pH cannot fall below 5.5 → hyperchloraemic NAGMA, hypokalaemia, nephrocalcinosis and calcium phosphate kidney stones (alkaline urine favours Ca₃(PO₄)₂ precipitation). Causes: Sjögren's, SLE, amphotericin B.</li>
        <li><b class="purple-term">Type 2 (Proximal RTA)</b>: PCT cannot reabsorb HCO₃⁻ → bicarbonaturia → NAGMA. Often part of <b class="purple-term">Fanconi syndrome</b> (generalised PCT dysfunction: also lose glucose, amino acids, uric acid, phosphate). Causes: multiple myeloma, Wilson's disease, cystinosis, tenofovir.</li>
        <li><b class="purple-term">Type 4 (Hyporeninhaemic hypoaldosteronism)</b>: low aldosterone (or resistance) → can't secrete K⁺ or H⁺ in collecting duct → <strong>hyperchloraemic NAGMA + hyperkalaemia</strong>. Most common RTA in clinical practice — seen in diabetic nephropathy and CKD.</li>
      </ul>
      <div class="highlight-box">RTA comparison:
        <ul>
          <li>Type 1: <em>distal, urine pH > 5.5, hypokalaemia, kidney stones</em></li>
          <li>Type 2: <em>proximal, bicarbonaturia, Fanconi syndrome, hypokalaemia</em></li>
          <li>Type 4: <em>hyporeninism/hypoaldosteronism, <strong>hyperkalaemia</strong>, most common in CKD/DM</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A patient with Sjögren's syndrome develops hyperchloraemic metabolic acidosis with a urine pH of 6.5. What is the diagnosis?",
      options: [
        "Type 2 (proximal) RTA — proximal HCO₃⁻ wasting",
        "Type 4 RTA — hypoaldosteronism and hyperkalaemia",
        "Type 1 (distal) RTA — inability to acidify urine below pH 5.5",
        "High anion gap metabolic acidosis from lactic acidosis",
      ],
      correct: 2,
      explanation:
        "Type 1 (distal) RTA is caused by a defect in H⁺ secretion by collecting duct intercalated cells. The urine cannot be acidified below pH 5.5 regardless of how acidotic the patient becomes. Sjögren's syndrome is a classic cause. Complications include nephrocalcinosis, calcium phosphate kidney stones, and hypokalaemia.",
    },
    {
      type: "question",
      text: "A patient with type 2 diabetes and CKD has metabolic acidosis with a normal anion gap and hyperkalaemia. What type of RTA does this represent?",
      options: [
        "Type 1 RTA",
        "Type 2 RTA",
        "Type 4 RTA",
        "Combined type 1 and 2 RTA",
      ],
      correct: 2,
      explanation:
        "Type 4 RTA (hyporeninhaemic hypoaldosteronism) is the most common RTA in clinical practice and occurs in diabetic nephropathy and CKD. Low renin → low aldosterone → the collecting duct cannot secrete K⁺ or H⁺. Result: hyperchloraemic NAGMA with hyperkalaemia. Unlike types 1 and 2, type 4 causes hyperkalaemia (not hypokalaemia).",
    },

    // ── Acute Kidney Injury ──────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Acute Kidney Injury (AKI)</h3>
      <p><b class="purple-term">AKI</b> is defined as an abrupt (within 48 hours) deterioration of kidney function:</p>
      <ul>
        <li>Rise in serum creatinine ≥ 26 µmol/L within 48h, or ≥ 1.5× baseline within 7 days</li>
        <li>Urine output < 0.5 mL/kg/h for ≥ 6 hours</li>
      </ul>
      <p>AKI is classified by the location of the cause:</p>
      <p><b class="purple-term">Prerenal AKI</b> (most common, ~55%): reduced blood flow to the kidneys → reduced GFR. The kidney itself is intact.
      <br/>Causes: hypovolaemia (haemorrhage, severe vomiting/diarrhoea, burns), cardiogenic shock, sepsis (vasodilation), renal artery stenosis.
      <br/>Labs: BUN/Creatinine ratio > 20:1 (urea rises disproportionately as tubules are still reabsorbing), urine osmolality > 500 mOsm/kg, urine Na⁺ < 20 mmol/L, fractional excretion of Na⁺ (FENa) <strong>< 1%</strong> (kidneys avidly retain Na⁺).
      <br/>Treatment: restore circulating volume (IV fluids), treat underlying cause.</p>
      <p><b class="purple-term">Intrinsic (intrarenal) AKI</b> (~40%): direct damage to kidney parenchyma.
      <br/>Most common cause: <strong>acute tubular necrosis (ATN)</strong> — ischaemic (prolonged prerenal) or nephrotoxic (aminoglycosides, contrast dye, myoglobin from rhabdomyolysis, NSAIDs).
      <br/>Also: glomerulonephritis, acute interstitial nephritis, vascular (HUS/TTP).
      <br/>Labs: BUN/Cr ratio < 20:1, urine Na⁺ > 40 mmol/L, FENa <strong>> 2%</strong> (damaged tubules cannot reabsorb Na⁺), muddy brown granular casts in urine (ATN).
      <br/>Treatment: supportive, remove nephrotoxins, treat underlying cause.</p>
      <p><b class="purple-term">Postrenal AKI</b> (~5%): obstruction to urine flow → back-pressure → ↓GFR.
      <br/>Causes: BPH (most common in elderly men), bilateral ureteric stones, tumour (bladder, cervix, prostate), retroperitoneal fibrosis. <strong>Must be bilateral</strong> (or single functioning kidney) to cause AKI.
      <br/>Diagnosis: hydronephrosis on renal ultrasound.
      <br/>Treatment: relieve obstruction (catheter, stent, nephrostomy).</p>
      <div class="highlight-box">Prerenal vs intrinsic AKI — key lab differences:
        <ul>
          <li>FENa < 1%, urine Na < 20, urine osmolality > 500 → <em>Prerenal</em></li>
          <li>FENa > 2%, urine Na > 40, urine osmolality ~ 300, muddy brown casts → <em>Intrinsic (ATN)</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A 70-year-old man with BPH is admitted with AKI. Renal ultrasound shows bilateral hydronephrosis. Urine output has been very low for 2 days. What is the most likely type of AKI and the immediate treatment?",
      options: [
        "Prerenal AKI — give IV fluids",
        "Intrinsic AKI — give loop diuretics",
        "Postrenal AKI — relieve obstruction with a urinary catheter",
        "Prerenal AKI — start fludrocortisone",
      ],
      correct: 2,
      explanation:
        "Bilateral hydronephrosis in a man with BPH indicates urinary outflow obstruction (postrenal AKI). The immediate treatment is to relieve the obstruction — a urinary catheter will drain the bladder and resolve the back-pressure. Post-obstructive diuresis (large urine output) can occur after relief and requires careful fluid management.",
    },
    {
      type: "question",
      text: "A hospitalised patient develops AKI after receiving IV contrast dye. FENa is 3%, urine Na⁺ is 45 mmol/L, and muddy brown casts are seen on urine microscopy. What type of AKI is this?",
      options: [
        "Prerenal AKI from contrast-induced hypotension",
        "Intrinsic AKI — contrast nephropathy causing acute tubular necrosis",
        "Postrenal AKI from ureteral spasm",
        "Prerenal AKI with preserved tubular function",
      ],
      correct: 1,
      explanation:
        "FENa > 2%, high urine Na⁺, and muddy brown granular casts are hallmarks of intrinsic AKI from acute tubular necrosis (ATN). Contrast dye causes direct tubular toxicity and renal vasoconstriction. Muddy brown casts represent sloughed-off tubular epithelial cells. Risk is reduced by pre-hydration with IV saline before contrast administration.",
    },
    {
      type: "question",
      text: "A patient with AKI develops the following complications: pH 7.20, K⁺ 7.1 mmol/L, urine output 60 mL/24h, and confusion. What is the first treatment that directly addresses the most life-threatening complication?",
      options: [
        "Sodium bicarbonate to correct the acidosis",
        "IV calcium gluconate to stabilise the cardiac membrane in the context of severe hyperkalaemia",
        "IV furosemide to increase urine output",
        "Oral sodium resonium to bind K⁺ in the gut",
      ],
      correct: 1,
      explanation:
        "K⁺ of 7.1 mmol/L is a cardiac emergency. The first step is always IV calcium gluconate to stabilise the myocardium — it acts within minutes. Then insulin + glucose, salbutamol, and bicarbonate are used to shift K⁺ into cells. With oliguria and no response to treatment, haemodialysis is indicated to definitively remove K⁺.",
    },

    // ── Chronic Kidney Disease ───────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Chronic Kidney Disease (CKD)</h3>
      <p><b class="purple-term">CKD</b> is defined as kidney damage or GFR < 60 mL/min/1.73m² persisting for <strong>≥ 3 months</strong>.</p>
      <p><strong>Staging (by GFR):</strong></p>
      <div class="highlight-box">CKD stages:
        <ul>
          <li>Stage 1: GFR ≥ 90 (normal GFR, markers of damage)</li>
          <li>Stage 2: GFR 60–89</li>
          <li>Stage 3a/3b: GFR 45–59 / 30–44</li>
          <li>Stage 4: GFR 15–29</li>
          <li>Stage 5 (ESRD): GFR < 15 → dialysis or transplant</li>
        </ul>
      </div>
      <p><strong>Main causes:</strong> Diabetic nephropathy (most common worldwide), hypertension, chronic glomerulonephritis, polycystic kidney disease (PKD), recurrent pyelonephritis.</p>
      <p><strong>Systemic complications</strong> — CKD affects every organ system:</p>
      <ul>
        <li><strong>Anaemia</strong>: ↓ EPO → normocytic normochromic anaemia. Treat with erythropoietin analogues + IV iron.</li>
        <li><strong>Renal osteodystrophy</strong>: ↓ calcitriol → ↓ Ca²⁺ → secondary hyperparathyroidism → bone disease. Treat with calcitriol, phosphate binders.</li>
        <li><strong>Metabolic acidosis</strong>: ↓ H⁺ excretion and ↓ NH₄⁺ production → NAGMA → treat with oral NaHCO₃.</li>
        <li><strong>Hyperkalaemia</strong>: ↓ aldosterone-driven K⁺ secretion.</li>
        <li><strong>Hypertension</strong>: RAAS activation, salt/water retention → treat with ACE inhibitors/ARBs (also renoprotective).</li>
        <li><strong>Uraemia</strong> (late CKD): accumulation of nitrogenous waste products → encephalopathy, pericarditis, platelet dysfunction (↑ bleeding), pruritis, uraemic frost.</li>
      </ul>
      <p><strong>Treatment principles:</strong> control blood pressure (ACE inhibitors/ARBs — first line, slow GFR decline), optimise glycaemia (SGLT2 inhibitors are renoprotective), manage anaemia and bone disease, avoid nephrotoxins (NSAIDs, contrast, aminoglycosides). When GFR < 15: haemodialysis, peritoneal dialysis, or renal transplant.</p>`,
    },
    {
      type: "question",
      text: "A patient with stage 4 CKD (GFR 22 mL/min) has anaemia, hyperkalaemia, and secondary hyperparathyroidism. Which single complication is the most immediate threat to life?",
      options: [
        "Anaemia — causing high-output cardiac failure",
        "Secondary hyperparathyroidism — causing pathological fractures",
        "Hyperkalaemia — causing life-threatening cardiac arrhythmias",
        "Metabolic acidosis — causing confusion",
      ],
      correct: 2,
      explanation:
        "Hyperkalaemia is the most immediately life-threatening complication of CKD because it can cause fatal cardiac arrhythmias (ventricular fibrillation, asystole) with little warning. While anaemia, hyperparathyroidism, and acidosis are all serious, their consequences develop over longer periods. Hyperkalaemia in CKD requires urgent treatment and may necessitate dialysis.",
    },
    {
      type: "question",
      text: "Which drug class is first-line for slowing the progression of CKD, particularly in diabetic nephropathy?",
      options: [
        "Loop diuretics",
        "ACE inhibitors or ARBs",
        "Calcium channel blockers",
        "Beta-blockers",
      ],
      correct: 1,
      explanation:
        "ACE inhibitors (or ARBs if ACE inhibitors are not tolerated) are first-line for slowing CKD progression in diabetic and non-diabetic CKD. By dilating the efferent arteriole, they reduce intraglomerular pressure and proteinuria, directly protecting remaining nephrons. They also control hypertension — a major driver of CKD progression. SGLT2 inhibitors are increasingly used as adjunct renoprotective therapy.",
    },

    // ── Nephrotic vs Nephritic Syndrome ─────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Nephrotic vs. Nephritic Syndrome</h3>
      <p>Glomerular disease presents as either <strong>nephrotic</strong> or <strong>nephritic</strong> syndrome — or a combination of both. Understanding the pathophysiology explains the clinical features.</p>
      <p><b class="purple-term">Nephrotic syndrome</b>: damage to the <strong>filtration barrier</strong> (podocytes, GBM charge) → large protein loss in urine:</p>
      <ul>
        <li><strong>Proteinuria</strong> > 3.5 g/day → frothy urine</li>
        <li><strong>Hypoalbuminaemia</strong> (albumin lost in urine) → ↓ plasma oncotic pressure → oedema (periorbital, peripheral, ascites)</li>
        <li><strong>Hyperlipidaemia</strong> (liver compensates for low oncotic pressure by making more lipoproteins)</li>
        <li><strong>Lipiduria</strong> (fatty casts, oval fat bodies on urine microscopy)</li>
        <li><strong>Hypercoagulability</strong>: antithrombin III, protein C, and protein S lost in urine → DVT, renal vein thrombosis, PE (especially membranous nephropathy)</li>
        <li><strong>Infection susceptibility</strong>: immunoglobulins and complement factors lost in urine</li>
      </ul>
      <p><b class="purple-term">Nephritic syndrome</b>: <strong>inflammatory</strong> glomerular damage → haematuria, proteinuria, hypertension, and oliguria:</p>
      <ul>
        <li><strong>Haematuria</strong> (dysmorphic RBCs, RBC casts in urine — casts formed in tubule)</li>
        <li><strong>Mild–moderate proteinuria</strong> (< 3.5 g/day typically)</li>
        <li><strong>Hypertension</strong> (Na⁺/water retention from reduced GFR)</li>
        <li><strong>Oliguria</strong> (reduced GFR)</li>
        <li><strong>Azotaemia</strong> (raised urea/creatinine)</li>
      </ul>
      <div class="highlight-box">Key causes to know:
        <ul>
          <li>Nephrotic in children: <em>Minimal change disease (MCD) — responds to steroids</em></li>
          <li>Nephrotic in adults: <em>Membranous nephropathy or FSGS</em></li>
          <li>Nephritic post-infection: <em>Post-streptococcal GN (1–3 weeks after URTI/skin)</em></li>
          <li>Nephritic + haematuria in young adults: <em>IgA nephropathy (during/after URTI)</em></li>
          <li>Nephritic + lung haemorrhage: <em>Goodpasture syndrome (anti-GBM disease)</em></li>
          <li>Rapidly progressive GN (RPGN): <em>crescents on biopsy — can reach ESRD in weeks</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A 4-year-old presents with periorbital puffiness, frothy urine, and hypoalbuminaemia. Urinalysis shows heavy proteinuria but no haematuria. What is the most likely diagnosis?",
      options: [
        "IgA nephropathy",
        "Post-streptococcal glomerulonephritis",
        "Minimal change disease",
        "Goodpasture syndrome",
      ],
      correct: 2,
      explanation:
        "This presentation is classic nephrotic syndrome in a child — proteinuria, hypoalbuminaemia, and oedema without haematuria. Minimal change disease (MCD) is the most common cause of nephrotic syndrome in children. It responds well to corticosteroids, and renal biopsy shows normal light microscopy with only foot process effacement on electron microscopy.",
    },
    {
      type: "question",
      text: "Why are patients with nephrotic syndrome at increased risk of thrombosis?",
      options: [
        "Hypoalbuminaemia increases blood viscosity",
        "Loss of antithrombin III, protein C, and protein S in the urine creates a hypercoagulable state",
        "Hyperlipidaemia causes platelet aggregation in glomerular vessels",
        "Low oncotic pressure causes stasis in peripheral veins",
      ],
      correct: 1,
      explanation:
        "In nephrotic syndrome, the leaky glomerular barrier allows small plasma proteins — including antithrombin III, protein C, and protein S — to be lost in the urine. These are natural anticoagulants. Their loss creates a hypercoagulable state, predisposing patients to DVT, pulmonary embolism, and renal vein thrombosis (particularly in membranous nephropathy).",
    },
    {
      type: "question",
      text: "A teenager develops haematuria with RBC casts and hypertension 2 weeks after a sore throat. What is the most likely diagnosis?",
      options: [
        "IgA nephropathy — haematuria during the infection",
        "Minimal change disease — nephrotic syndrome after infection",
        "Post-streptococcal glomerulonephritis — nephritic syndrome 1–3 weeks after infection",
        "Goodpasture syndrome — haematuria with lung haemorrhage",
      ],
      correct: 2,
      explanation:
        "The 2-week latent period after a streptococcal sore throat followed by nephritic features (haematuria with RBC casts, hypertension, oliguria) is the classic presentation of post-streptococcal glomerulonephritis (PSGN). It is caused by immune complex deposition in the glomerulus. IgA nephropathy presents with haematuria during (not after) the infection — a key distinguishing feature.",
    },
    {
      type: "question",
      text: "What distinguishes IgA nephropathy from post-streptococcal glomerulonephritis in terms of the timing of haematuria?",
      options: [
        "IgA nephropathy presents 2–3 weeks after infection; PSGN presents during the infection",
        "IgA nephropathy presents during or immediately after an upper respiratory infection (synpharyngitic); PSGN presents 1–3 weeks after infection",
        "Both present during the infection — the difference is the type of infection",
        "IgA nephropathy presents with nephrotic features; PSGN presents with nephritic features",
      ],
      correct: 1,
      explanation:
        "IgA nephropathy (Berger's disease) classically presents with synpharyngitic haematuria — haematuria occurring during or immediately after (within 1–2 days) an URTI, as pre-formed IgA complexes are deposited. PSGN has a latent period of 1–3 weeks post-streptococcal infection while immune complexes form and deposit. This timing difference is a high-yield exam distinction.",
    },
  ],
};
